import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../api/Api";
import { Plus, Edit2, Save, CheckCircle, XCircle } from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css"; 

const FeedbackCategoryListPage = () => {
  const [items, setItems] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchItems = async () => {
    try {
      const res = await api.get("/adminmod/feedback-category-list");
      setItems(res.data?.data || []);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleStatusChange = async (id, value) => {
    try {
      // Optimistic update
      const updatedItems = items.map((item) =>
        item.id === id ? { ...item, is_active: value } : item
      );
      setItems(updatedItems);

      await api.put("/adminmod/feedback-category-status", {
        id,
        is_active: value,
      });
      toast.success("Status updated");
    } catch {
      toast.error("Status update failed");
      fetchItems(); // Revert on error
    }
  };

  const handleEditSave = async (id) => {
    if (!editName.trim()) {
      toast.error("Category name is required");
      return;
    }

    try {
      await api.put("/adminmod/feedback-category-edit", {
        id,
        item_names: editName,
      });
      toast.success("Category updated successfully");
      setEditId(null);
      fetchItems();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="container-fluid py-3 bg-white min-vh-100">
      
      {/* HEADER CARD */}
      <div className="card shadow-sm border-0 bg-primary-subtle mb-4 rounded-3">
        <div className="card-body d-flex align-items-center justify-content-between p-3">
          <div>
            <h4 className="mb-1 fw-bold text-dark">Manage Feedback Categories</h4>
            
          </div>
          
          <Link
            to="/master/feedback-category-add"
            className="btn btn-primary btn-sm d-flex align-items-center gap-2 px-3 fw-medium"
          >
            <Plus size={16} /> Add Category
          </Link>
        </div>
      </div>

      {/* TABLE CARD */}
      <div className="card shadow-sm border-0 rounded-3 overflow-hidden">
        <div className="table-responsive">
          <table className="table table-hover table-sm align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th className="py-3 px-3 text-secondary text-uppercase small fw-bold" style={{ width: "10%" }}>#</th>
                <th className="py-3 text-secondary text-uppercase small fw-bold" style={{ width: "60%" }}>Category Name</th>
                <th className="py-3 text-center text-secondary text-uppercase small fw-bold" style={{ width: "15%" }}>Action</th>
                <th className="py-3 text-center text-secondary text-uppercase small fw-bold" style={{ width: "15%" }}>Status</th>
              </tr>
            </thead>
            
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="text-center py-5 text-muted">
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                    Loading data...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-5 text-muted">
                    No categories found.
                  </td>
                </tr>
              ) : (
                items.map((item, index) => (
                  <tr key={item.id} className={editId === item.id ? "table-active" : ""}>
                    <td className="px-3 text-muted small fw-bold">{index + 1}</td>

                    {/* CATEGORY NAME */}
                    <td>
                      {editId === item.id ? (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          autoFocus
                        />
                      ) : (
                        <span className="fw-medium text-dark">{item.item_names}</span>
                      )}
                    </td>

                    {/* ACTION BUTTONS */}
                    <td className="text-center">
                      {editId === item.id ? (
                        <button
                          className="btn btn-success btn-sm d-inline-flex align-items-center gap-1 shadow-sm"
                          onClick={() => handleEditSave(item.id)}
                          title="Save"
                        >
                          <Save size={14} /> Save
                        </button>
                      ) : (
                        <button
                          className="btn btn-light btn-sm text-primary border-0 hover-shadow"
                          onClick={() => {
                            setEditId(item.id);
                            setEditName(item.item_names);
                          }}
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                      )}
                    </td>

                    {/* STATUS TOGGLE */}
                    <td className="text-center">
                      <select
                        className={`form-select form-select-sm fw-bold border-0 ${
                          item.is_active == 1 ? "text-success bg-success-subtle" : "text-danger bg-danger-subtle"
                        }`}
                        style={{ width: "110px", margin: "0 auto", cursor: "pointer" }}
                        value={item.is_active}
                        onChange={(e) => handleStatusChange(item.id, e.target.value)}
                        disabled={editId === item.id}
                      >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <div className="card-footer bg-white py-3">
          <small className="text-muted">Showing {items.length} records</small>
        </div>
      </div>
    </div>
  );
};

export default FeedbackCategoryListPage;